import classNames from 'classnames';
import React from 'react';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  getSampleStatusTooltip,
  doesCurrentUserHaveAccessForItem,
  isItemAvailable,
  doesItemHaveBlockingStatusWarning,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {openLocationExpandedSearch} from 'samples/StorableActions';
import getValidationErrorText from 'schemas/getValidationErrorText';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import SmartChip from 'ui-library/chips/SmartChip';
import useSmartChipQuery, {SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';
import {StorableChipPopoverContent} from './StorableChipPopoverContent';

type LocationChipSummary = ExtractByTypename<StorableChipSummaryGraphQL, 'LocationNode'>;

type ContainerChipSummary = ExtractByTypename<StorableChipSummaryGraphQL, 'ContainerNode'>;

export interface StorableChipProps extends SharedSmartChipProps<SmartChipType.STORABLE> {
  /** The API identifier of the storable */
  id: string;
  /**
   * A function to override how this storable's contents are rendered in the fancy chip popover. By default,
   * the popover renders a BenchChip for each content. Intended for cases where a custom visual treatment for
   * each content is necessary, as with Plate Maps, which inject a color-coded swatch next to each content
   * chip.
   */
  popoverContainedItemRenderer?: (id: string) => JSX.Element;
}

function isReadonly(storable?: StorableChipSummaryGraphQL): boolean {
  if (!storable) {
    return false;
  }
  return !isFeatureEnabled({feature: StandardFeature.OPEN, item: storable});
}

function isContainerChipSummary(storable: StorableChipSummaryGraphQL): storable is ContainerChipSummary {
  return storable.__typename === 'ContainerNode';
}

function isLocationChipSummary(storable: StorableChipSummaryGraphQL): storable is LocationChipSummary {
  return storable.__typename === 'LocationNode';
}

export default function StorableChip(props: StorableChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item,
    handleClickLink,
    isInaccessible,
    itemUrl: storableUrl,
  } = useSmartChipQuery<SmartChipType.STORABLE>({
    id,
    onClick,
    getUrlFn: (storable) =>
      isContainerChipSummary(storable) || isLocationChipSummary(storable) ? storable.url : '',
  });
  const storable = item != null ? {...item, parentStorable: item.authenticatedParentStorable} : item;

  const validationState = storable ? storable.validationState : null;
  const validationExtras = {errorRecords: storable?.validationState?.validationErrorRecords ?? null};

  const handleClickLinkEnhanced = (e: BasicEvent): void => {
    if (storable) {
      if (props.onClick != null) {
        handleClickLink(e);
      } else {
        e.stopPropagation();
        if (isLocationChipSummary(storable)) {
          openLocationExpandedSearch(graphQLToREST('LocationSummary', storable));
        } else {
          FileBrowserActions.openFile(storable.id, {folderItem: {...storable}});
        }
      }
    }
  };

  const showUnauthorized =
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) && !doesCurrentUserHaveAccessForItem(storable);
  const showUnavailable = !isItemAvailable(storable);
  const validationProps = {
    negative: isFailed(validationState),
    tooltipTitleOverride: getValidationErrorText(validationState, validationExtras),
  };
  const checkOutStatusProps =
    showUnauthorized || showUnavailable
      ? {
          tooltipTitleOverride: getSampleStatusTooltip(storable!, {
            includeAvailability: showUnavailable,
            includeAuthorization: showUnauthorized,
            hideAssignee: false,
          }),
          className: classNames(
            props.className,
            doesItemHaveBlockingStatusWarning(storable!) ? 'chip--negative' : 'chip--warningLighter'
          ),
        }
      : {};

  const showExpired = storable
    ? BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
      isContainerChipSummary(storable) &&
      storable.expirationInfo.isTrackingEnabled &&
      storable.expirationInfo.isExpired
    : false;
  const expirationStatusProps = showExpired ? {className: 'chip--warningLighter'} : {};

  return (
    <SmartChip
      {...sharedSmartChipProps}
      {...validationProps}
      {...expirationStatusProps}
      {...checkOutStatusProps}
      icon={(storable?.storableSchema?.iconId as BenchlingIconName) ?? null}
      item={storable}
      isInaccessible={isInaccessible}
      isReadonly={isReadonly(storable)}
      onClick={handleClickLinkEnhanced}
      url={storableUrl}
      popoverContentOverride={
        <StorableChipPopoverContent
          id={props.id}
          isInaccessible={isInaccessible}
          storable={storable}
          containedItemRenderer={props.popoverContainedItemRenderer}
        />
      }
    />
  );
}
